import React, {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  AmountInput,
  Box,
  EntitySelector,
  EntitySelectorAutoTabs,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectPopup,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
  type EntitySelectorConfig,
  type IconName,
} from "@salestrak/ui";

import {
  useCustomFieldPool,
  useStagePool,
  useTeamPool,
  useUserPool,
  useWorkflowPool,
} from "#app/components/entity-selectors";
import { toEntityTriggerSize } from "#app/components/entity-selectors/sizes";
import { EntityTag } from "#app/components/entity";
import {
  groupOptionRows,
  runsBy,
  type OptionRow,
} from "#app/components/automations/option-groups";
import {
  FieldGutterProvider,
  useFieldGutter,
} from "#app/components/automations/canvas/field-align";
import { fieldChangeFilterMode } from "#app/components/automations/field-capabilities";
import { useCustomFieldsList } from "#app/lib/hooks/use-custom-fields-list";
import { connectionRows } from "#app/lib/data";
import {
  buildCustomFieldDefMap,
  moneyInputProps,
  numberInputProps,
  toNumericValue,
  type CustomFieldDef,
} from "#app/lib/custom-field-utils";
import {
  useAutomationInboundSources,
  connectionDisplayName,
  type InboundSource,
} from "#app/hooks/automations/use-automation-inbound-sources";
import { brandLogo } from "#app/components/integrations/brand-logos";
import { at } from "#app/lib/utils/at";
import { useMemberDirectory } from "#app/lib/data";
import { resolveEntityLabel } from "#app/lib/entity-visuals";
import { BrandLogoTile } from "#app/components/integrations/integrations-config-primitives";
import type { Omittable } from "@salestrak/ui";

type RelatedKind = "none" | "stage" | "workflow" | "assignee" | "field";

type ActionDef = Omittable<{
  sourceKey: string;
  label: string; // verb phrase that reads inside the sentence
  related: RelatedKind;
  // SalesTrak glyph shown beside the event in the fields-layout dropdown.
  icon: IconName;
  // Section header the event lists under in the Event dropdown (groups render
  // in first-appearance order; ungrouped events list last).
  group?: string;
  // When set, the fields-layout Event dropdown labels this event as
  // "<Source> <subjectLabel>" (e.g. "Project created"), adapting to the
  // selected source. Other surfaces still use `label`.
  subjectLabel?: string;
  // A standalone label for the fields-layout Event dropdown (no source prefix),
  // e.g. "Comment added". Used when `subjectLabel` is absent; the sentence
  // layout still uses `label`.
  eventLabel?: string;
  // Reads as a possessive — "a project's <X>" (e.g. its field / address).
  possessive?: boolean;
}>;

// `{subject}` in a group label is replaced with the UPPERCASE selected source
// ("PROJECT MANAGEMENT").
const ACTIONS: ActionDef[] = [
  { sourceKey: "issue.created", label: "is created", related: "none", icon: "circle-plus", group: "CREATE", subjectLabel: "created" },
  { sourceKey: "note.added", label: "has a comment added", related: "none", icon: "message", group: "COMMUNICATION", eventLabel: "Comment added" },
  { sourceKey: "issue.field_changed", label: "field is changed", related: "field", possessive: true, icon: "pencil", group: "{subject} MANAGEMENT", eventLabel: "Custom Field changed" },
  { sourceKey: "issue.assigned", label: "changes assignee", related: "assignee", icon: "user-plus", group: "{subject} MANAGEMENT", eventLabel: "Assignee changed" },
  { sourceKey: "issue.address_changed", label: "address is changed", related: "none", possessive: true, icon: "map-pin", group: "{subject} MANAGEMENT", eventLabel: "Address changed" },
  { sourceKey: "issue.stage_changed", label: "changes stage", related: "stage", icon: "arrow-right-left", group: "{subject} MANAGEMENT", eventLabel: "Stage changed" },
  { sourceKey: "issue.workflow_changed", label: "changes workflow", related: "workflow", icon: "workflow", group: "{subject} MANAGEMENT", eventLabel: "Workflow changed" },
  { sourceKey: "issue.archived", label: "is archived", related: "none", icon: "archive", group: "{subject} MANAGEMENT", subjectLabel: "archived" },
];

// Events always have a value — a new trigger defaults to "project is created".
export const DEFAULT_SOURCE_KEY = "issue.created";
// The stage-changed event — the default trigger for automations created FROM a
// workflow (and the one "Automate stage" seeds pinned to a stage).
export const STAGE_CHANGED_SOURCE_KEY = "issue.stage_changed";
// A connection-sourced trigger: the Source is an inbound integration connection
// and the config carries { connectionId, topic } instead of the issue `entity`.
// Matches the server catalog entry in catalog/trigger-sources.ts.
export const INTEGRATION_INBOUND_SOURCE_KEY = "integration.inbound";

// How tall a field popup may grow before its list scrolls.
//
// 360 was leaving the Action list 189px short of its content, so it opened
// already scrolled to the selected row with the first row clipped at the top
// edge — and a clipped row is the one a user aims at first. 560 clears the
// deepest of these lists (513px of rows plus the 36px search field) so it opens
// whole and never scrolls. A longer list still scrolls, and the pool's own
// ScrollArea caps it at 65vh, so this never runs past a short viewport.
const FIELD_POPUP_MAX_HEIGHT = 560;

// Shared by every grouped/searchable select on the automation surfaces — the
// trigger's Source/Event dropdowns, the Action node's Action dropdown, and the
// add-step "+" popover (action-picker.tsx) all speak this option shape.
export type Option = Omittable<{
  value: string;
  label: string;
  disabled?: boolean;
  icon?: IconName;
  // A leading brand-logo image URI, preferred over `icon` when present — the
  // trigger's Source dropdown shows an integration connection's provider logo,
  // falling back to the provider's registry icon when we bundle no brand mark.
  logo?: string;
  // Extra terms this option matches on beyond its label. Only meaningful on a
  // `searchable` FieldSelect, which routes through EntitySelector — whose
  // `filterEntityItems` substring-matches keywords. The connection-bearing lists
  // use it so a connection is findable by its provider name.
  keywords?: string[];
  // Muted subtext rendered inline after the row's label, on both render paths
  // (a searchable FieldSelect's filter also matches on it). The webhook
  // payload's Source rows use it for the node's unique handle — the slug on
  // the canvas badge.
  description?: string;
  // Section label, and `subgroup` the indented sub-heading under it — the two
  // heading levels the pool renders. The Action dropdown spends them on source →
  // action category (a built-in) or provider → connection (an integration).
  // Bucketing and ordering live in option-groups.ts.
  group?: string;
  subgroup?: string;
  // What the CLOSED control shows, when that differs from the row's own label.
  // An integration action's row reads "Log connection action" because the
  // connection is the heading right above it; collapsed, that context is gone,
  // so the trigger reads "Log connection action · Bartlett Main".
  triggerLabel?: string;
  // Render above every section, outside all collapse state. For a current value
  // that has no place in the tree (a step whose connection was revoked), which
  // must still be nameable. Searchable path only — `Select` has no pinned row,
  // so a pinned option lands in its normal position there.
  pinned?: boolean;
}>;

// Top-level entity the automation concerns — only Project exists. The noun is
// capitalized as a proper noun both in the Source dropdown and inside the
// prose sentence ("When a Project is created").
const ENTITIES: Option[] = [
  { value: "project", label: "Project" },
];
export const DEFAULT_ENTITY = "project";

const SOURCE_OPTIONS: Option[] = ENTITIES;

// Plain-language description of when a trigger fires, using "any"/"anyone"
// where an optional related object is unset. `resolve(entityType, id)` turns a
// stored id into a display name (or null). Pure — reused by the editor summary
// and the canvas trigger node.
export function summarizeTrigger(
  sourceKey: string | null,
  config: Record<string, unknown>,
  resolve: (entityType: string, id: string) => string | null,
): string | null {
  if (!sourceKey) return null;
  const entity = typeof config.entity === "string" ? config.entity : DEFAULT_ENTITY;
  const entityLabel = ENTITIES.find((e) => e.value === entity)?.label ?? "Project";
  const str = (k: string) => (typeof config[k] === "string" ? (config[k] as string) : null);

  switch (sourceKey) {
    case "issue.created":
      return `a ${entityLabel} is created`;
    case "issue.stage_changed": {
      const wfId = str("workflowId");
      const wf = wfId ? resolve("workflow", wfId) : null;
      const suffix = wf ? ` in ${wf}` : "";
      const fromId = str("fromStageId");
      const toId = str("toStageId");
      const from = fromId ? (resolve("stage", fromId) ?? "a stage") : null;
      const to = toId ? (resolve("stage", toId) ?? "a stage") : null;
      if (from && to) return `a ${entityLabel} moves from ${from} to ${to}${suffix}`;
      if (from) return `a ${entityLabel} moves from ${from}${suffix}`;
      if (to) return `a ${entityLabel} moves to ${to}${suffix}`;
      return `a ${entityLabel} changes stage${suffix}`;
    }
    case "issue.workflow_changed": {
      // Each side reads as its workflow › stage pair when a stage is pinned.
      const part = (wfKey: string, stKey: string): string | null => {
        const wfId = str(wfKey);
        if (!wfId) return null;
        const wf = resolve("workflow", wfId) ?? "a workflow";
        const stId = str(stKey);
        const st = stId ? (resolve("stage", stId) ?? "a stage") : null;
        return st ? `${wf} › ${st}` : wf;
      };
      const from = part("fromWorkflowId", "fromStageId");
      const to = part("toWorkflowId", "toStageId");
      if (from && to) return `a ${entityLabel} moves from ${from} into ${to}`;
      if (to) return `a ${entityLabel} moves into ${to}`;
      if (from) return `a ${entityLabel} moves out of ${from}`;
      return `a ${entityLabel} changes workflow`;
    }
    case "issue.assigned": {
      const aid = str("assigneeId");
      const who = aid ? (resolve("assignee", aid) ?? "someone") : "anyone";
      return `a ${entityLabel} is assigned to ${who}`;
    }
    case "issue.field_changed": {
      const fid = str("fieldKey");
      if (!fid) return `any of a ${entityLabel}'s fields is changed`;
      const base = `a ${entityLabel}'s ${resolve("customField", fid) ?? "field"}`;
      // Best-effort display of the from/to value filters. Member-field ids
      // resolve to names, checkbox booleans read as Checked/Unchecked, and
      // anything unresolvable renders as its raw value.
      const fmt = (value: unknown): string | null => {
        const parts = (Array.isArray(value) ? value : [value])
          .filter((v) => v != null && v !== "" && typeof v !== "object")
          .map((v) =>
            typeof v === "boolean"
              ? v
                ? "Checked"
                : "Unchecked"
              : (resolve("assignee", String(v)) ?? String(v)),
          );
        return parts.length ? parts.join(", ") : null;
      };
      const from = fmt(config.fromValue);
      const to = fmt(config.toValue);
      if (from && to) return `${base} changes from ${from} to ${to}`;
      if (to) return `${base} changes to ${to}`;
      if (from) return `${base} changes from ${from}`;
      return `${base} is changed`;
    }
    case "issue.address_changed":
      return `a ${entityLabel}'s address is changed`;
    case "note.added":
      return `a comment is added to a ${entityLabel}`;
    case "issue.archived":
      return `a ${entityLabel} is archived`;
    case INTEGRATION_INBOUND_SOURCE_KEY: {
      // "the <Connection Name> connection receives <Event>". Both parts resolve
      // through the caller's `resolve` — "connection" for the id → name and
      // "inboundTopic" for the topic key → its friendly label. A surface that
      // hasn't wired those (or whose connection list is still loading) falls
      // back to "a connection" / the raw topic key.
      const cid = str("connectionId");
      const name = cid ? resolve("connection", cid) : null;
      const topicKey = str("topic");
      const event = topicKey ? (resolve("inboundTopic", topicKey) ?? topicKey) : "an event";
      return name
        ? `the ${name} connection receives ${event}`
        : `a connection receives ${event}`;
    }
    default:
      return null;
  }
}

// The friendly event name shown in the trigger node's Event dropdown — either
// subject-prefixed ("Project created") or a standalone event label ("Stage
// changed"). Exported so other surfaces (e.g. the Manage-tab trigger filter)
// can name trigger sources identically to the builder rather than re-deriving
// from the raw catalog label. `subject` defaults to the live entity noun.
export function triggerEventLabel(sourceKey: string, subject = "Project"): string {
  // Connection-sourced trigger: this signature carries no config, so the chosen
  // topic can't be named here. Surfaces that hold the config (the canvas node,
  // the editor summary) label it via `summarizeTrigger`; the rest get the
  // generic catalog label.
  if (sourceKey === INTEGRATION_INBOUND_SOURCE_KEY) return "Integration event received";
  const a = ACTIONS.find((x) => x.sourceKey === sourceKey);
  if (!a) return sourceKey;
  return a.subjectLabel ? `${subject} ${a.subjectLabel}` : (a.eventLabel ?? a.label);
}

// The draft the editor reports up on every change.
export type TriggerDraft = {
  sourceKey: string | null;
  config: Record<string, unknown>;
  complete: boolean;
  name: string;
  summary: string | null;
};

// Shared inline token look — a bordered, transparent chip that fills only on
// hover (no resting background, no chevron). 14px medium; default color when
// filled, muted as a placeholder. 2px vertical padding; 6px horizontal is off
// the 4→8 spacing scale, so the left/right insets are exact-width spacers.
//
// When empty (no `label` — an unmade choice or an unresolved placeholder) the
// chip borrows the "Add step" look: a DASHED border + muted text, signalling
// the user still has to pick something here. A filled chip gets the solid
// border, reading as a settled value.
//
// Returned as a Box element (not a wrapper component) so the Select /
// EntitySelector trigger's `render` press + ref forwarding lands on it. Both
// those triggers declare it non-native (`nativeButton={false}`), so Base UI
// supplies the button role and key handling for this div.
export function tokenChip(
  label: string | null | undefined,
  placeholder: string,
  disabled?: boolean,
) {
  const empty = !label;
  return (
    <Box
      direction="row"
      align="center"
      border
      // Box has no border-style, opacity or pointer-events props, so all three
      // are authored classes.
      className={`${empty ? "border-dashed" : ""}${disabled ? " pointer-events-none opacity-50" : ""}`}
      hoverBackground
      paddingY="2xs"
      rounded="md"
    >
      <Box width={6} />
      <Text size="sm" truncate={1} variant={empty ? "muted" : undefined} weight="medium">
        {label || placeholder}
      </Text>
      <Box width={6} />
    </Box>
  );
}

// An inline, sentence-embedded plain select (for non-entity tokens).
function InlineSelect({
  value,
  options,
  placeholder,
  displayLabel,
  tooltip,
  onSelect,
}: Omittable<{
  value: string | null;
  options: Option[];
  placeholder: string;
  displayLabel?: string;
  /** Hover hint describing what this token controls. */
  tooltip?: string;
  onSelect: (value: string | null) => void;
}>) {
  const current = options.find((o) => o.value === value) ?? null;
  const triggerChip = (
    <SelectTrigger
      chevron={false}
      // `tokenChip` is a Box (a div), shared with the EntitySelector trigger
      // beside it — which opts out of native-button semantics for the same
      // reason: this chrome may host nested interactive elements. Base UI
      // applies the button role and key handling itself when told the element
      // is not a native <button>; left at the default it expects one and warns.
      nativeButton={false}
      render={tokenChip(
        current ? (displayLabel ?? current.label) : null,
        placeholder,
      )}
    />
  );
  return (
    // Select is value-keyed: the item's value is a plain string.
    <Select
      onValueChange={(next: string | null) => onSelect(next || null)}
      value={current?.value ?? ""}
    >
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger>{triggerChip}</TooltipTrigger>
          <TooltipPopup side="top">
            <Text size="xs">{tooltip}</Text>
          </TooltipPopup>
        </Tooltip>
      ) : (
        triggerChip
      )}
      <SelectPopup>
        {options.map((o, i) => (
          <SelectItem key={`${o.value}-${i}`} disabled={o.disabled} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectPopup>
    </Select>
  );
}

// An inline, sentence-embedded ENTITY select — same link-token look, backed by
// entity-selector pools (avatars/icons in the list).
export function InlineEntitySelect({
  pools,
  value,
  placeholder,
  disabled,
  onChange,
}: Omittable<{
  pools: EntityPoolConfig[];
  value: string | null;
  placeholder: string;
  disabled?: boolean;
  onChange: (id: string | null) => void;
}>) {
  const config = useMemo<EntitySelectorConfig>(
    () => ({ pools, maxSelections: 1 }),
    [pools],
  );
  const current = useMemo(() => {
    if (!value) return null;
    for (const p of pools) {
      const item = findPoolItem(p, value);
      if (item) return item;
    }
    return null;
  }, [pools, value]);
  const selections = useMemo<EntitySelection[]>(() => {
    if (!value) return [];
    for (const p of pools) {
      const entity = findPoolItem(p, value);
      if (entity) return [{ id: value, poolId: p.id, entity }];
    }
    return [];
  }, [pools, value]);
  return (
    // `group/token` is what the clear badge's hover-reveal keys off; the reveal
    // itself is CSS, and `pointer-coarse:` keeps it visible on touch.
    <Box group="token" position="relative">
      <EntitySelector
        title={pools.length === 1 ? pools[0]?.label : undefined}
        config={config}
        onValueChange={(next) => onChange(next[0]?.id ?? null)}
        value={selections}
      >
        <EntitySelectorTrigger
          render={tokenChip(current?.title, placeholder, disabled)}
        />
        <EntitySelectorContent maxHeight={FIELD_POPUP_MAX_HEIGHT}>
          <EntitySelectorInput />
          {pools.length > 1 ? <EntitySelectorAutoTabs /> : null}
          {pools.map((p) => (
            <EntitySelectorPool key={p.id} value={p.id} />
          ))}
          <EntitySelectorEmpty>Nothing found</EntitySelectorEmpty>
        </EntitySelectorContent>
      </EntitySelector>
      {value && !disabled ? <ClearBadge onClear={() => onChange(null)} /> : null}
    </Box>
  );
}

// A small circular × that floats over a filled token's top-right corner. It
// overlays the chip (absolute) and fades in on hover of the surrounding
// hover-group, so a resting token reads as clean text and the affordance only
// appears when the pointer is on it — or, on touch, once a tap on the token
// has armed it.
export function ClearBadge({ onClear }: { onClear: () => void }) {
  return (
    <Box<"button">
      hoverReveal="token" shadow="sm" align="center"
      aria-label="Clear selection"
      as="button"
      surface="card"
      border
      borderColor="border"
      cursor="pointer"
      height={18}
      justify="center"
      onClick={onClear}
      position="absolute"
      right={-6}
      rounded="full"
      top={-6}
      width={18}
      zIndex={1}
    >
      <Icon color="muted" name="x" size="xs" />
    </Box>
  );
}

// Non-interactive sentence words ("When a", "›"). 14px regular, lightly muted.
export function Word({ children }: { children: string }) {
  return (
    <Text size="sm" weight="normal" variant="muted">
      {children}
    </Text>
  );
}

// A labelled field for the stacked "fields" trigger layout (the related config
// after the Event input): the 12px medium caption sits ABOVE the control.
// `required` appends a red "*" so a required field reads at a glance; the
// caption itself stays muted. `info` hangs a muted info glyph beside the
// caption whose tooltip carries a clarifying note (e.g. that the webhook
// payload preview shows sample data).
/** The row's label, read by the selectors inside it as their phone sheet's
 *  caption — the row names what the picker lists. */
const FieldRowLabelContext = createContext<string | undefined>(undefined);

export function FieldRow({
  label,
  required = false,
  info,
  children,
}: Omittable<{
  label: string;
  required?: boolean;
  info?: string;
  children: ReactNode;
}>) {
  return (
    <Box direction="column" gap="sm" minWidth={0}>
      <Box align="center" direction="row" gap="2xs">
        <Text size="xs" weight="medium" variant="muted">
          {label}
          {required ? (
            <Text size="xs" variant="destructive" weight="medium">
              {" *"}
            </Text>
          ) : null}
        </Text>
        {info ? (
          <Tooltip>
            <TooltipTrigger
              aria-label="More information"
              render={<Box align="center" />}
            >
              <Icon color="muted" name="info" size="xs" />
            </TooltipTrigger>
            <TooltipPopup className="max-w-60" side="top">
              <Text size="xs">{info}</Text>
            </TooltipPopup>
          </Tooltip>
        ) : null}
      </Box>
      <FieldRowLabelContext.Provider value={label}>{children}</FieldRowLabelContext.Provider>
    </Box>
  );
}

// The pool id a searchable FieldSelect's single pool carries. It is never shown
// — one pool means no tabs — so it only has to be stable.
const OPTION_POOL_ID = "options";

// `Option[]` → the one `EntityPoolConfig` a searchable FieldSelect renders.
//
// `Select` ships no in-popup search field, so a searchable FieldSelect goes
// through EntitySelector instead — which filters, and which is already what the
// entity fields beside these controls use, so the two read as one system.
// `Option` maps onto `EntityItemConfig` almost field for field.
//
// `group`/`subgroup` become the section and sub-section labels, bucketed by
// `groupOptionRows` — the same rows `GroupedOptions` walks, so switching on
// `searchable` never reorders a list. With `collapsibleSections` each level folds
// on its own.
//
// `collapsedGroups` open folded; `keepOpenGroup` is excluded from them, so the
// section holding the current selection is never one of the folded ones.
function optionsToPool(
  options: Option[],
  collapsedGroups: string[] = [],
  keepOpenGroup?: string,
): EntityPoolConfig {
  const toItem = (o: Option, section?: string, subsection?: string) => ({
    id: o.value,
    title: o.label,
    section,
    subsection,
    disabled: o.disabled,
    keywords: o.keywords,
    description: o.description,
    // A brand logo wins over the registry glyph, exactly as the trigger resolves
    // it. `padded` puts an interface icon in a rounded tile rather than inside an
    // avatar circle — these rows are options, not people.
    avatar: o.logo
      ? { render: () => <BrandLogoTile logo={o.logo as string} /> }
      : o.icon
        ? { icon: o.icon, padded: true }
        : undefined,
  }) satisfies EntityItemConfig;

  // Pinned rows render above the sections, so they are taken out before the rows
  // are bucketed — a pinned option must not open a heading of its own.
  const pinnedItems = options.filter((o) => o.pinned).map((o) => toItem(o));
  const items = groupOptionRows(options.filter((o) => !o.pinned)).map((row) =>
    toItem(row.option, row.section, row.subsection),
  );

  return {
    id: OPTION_POOL_ID,
    // A pool label would render as a header above a flat list; these controls
    // are labelled by their FieldRow already.
    label: "",
    items,
    pinnedItems,
    // Nothing here is a person, so the avatar column only earns its width when
    // some option actually carries a glyph.
    hideAvatars: ![...items, ...pinnedItems].some((i) => i.avatar),
    // Fold by section: a Source list with several connected providers is worked
    // one provider at a time.
    collapsibleSections: true,
    defaultCollapsedSections: collapsedGroups.filter((g) => g !== keepOpenGroup),
  };
}

// The fields layout uses the app's STANDARD dropdowns (chevron-bearing Select /
// EntitySelector triggers) rather than the sentence's chevron-less link chips —
// the trigger node reads as a real form. `FieldSelect` is the plain-option one.
export function FieldSelect({
  value,
  options,
  placeholder,
  onSelect,
  fullWidth = true,
  size = "sm",
  valueWeight = "regular",
  hoverBorder = false,
  ghost = false,
  emphasized = false,
  iconSize = "xs",
  searchable = false,
  searchPlaceholder = "Search…",
  emptyText = "No matches",
  collapsedGroups,
}: Omittable<{
  value: string | null;
  options: Option[];
  placeholder: string;
  onSelect: (value: string | null) => void;
  fullWidth?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  valueWeight?: "regular" | "medium" | "semibold" | "bold";
  // Strengthen the border on hover, for a control that must advertise itself as
  // editable before it is touched.
  hoverBorder?: boolean;
  // Transparent (bg-less) trigger for secondary/subset selects — matches the
  // ghost EntitySelector triggers. The primary select stays filled.
  ghost?: boolean;
  // The node's primary/first control — fills with `muted` in light mode only
  // (no change in dark) so it stands apart from the fields below it.
  emphasized?: boolean;
  iconSize?: "xs" | "sm" | "md" | "lg";
  // Renders through EntitySelector instead of Select, which adds the in-popup
  // search field (matching on label, keywords and value) and makes each section
  // group heading a fold. Off by default — a three-row list does not need one.
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  // Sections that open folded, for a list long enough that everything expanded
  // buries the common reach. Whichever of them holds the current selection is
  // dropped here rather than at the call site: a collapsed selection is not only
  // invisible, it is where the keyboard cursor starts.
  collapsedGroups?: string[];
}>) {
  const rowLabel = useContext(FieldRowLabelContext);
  const current = findOption(options, value);
  const gutter = useFieldGutter();

  const pool = useMemo(
    () => optionsToPool(options, collapsedGroups, current?.group),
    [options, collapsedGroups, current?.group],
  );
  const config = useMemo<EntitySelectorConfig>(
    () => ({ pools: [pool], maxSelections: 1, searchPlaceholder }),
    [pool, searchPlaceholder],
  );
  const selections = useMemo<EntitySelection[]>(() => {
    const entity = value ? findPoolItem(pool, value) : null;
    if (!entity) return [];
    // The trigger renders the item's own title, so a distinct collapsed label is
    // applied here — one place, both render paths agreeing with each other.
    const collapsed = current?.triggerLabel;
    return [
      {
        id: entity.id,
        poolId: pool.id,
        entity: collapsed ? { ...entity, title: collapsed } : entity,
      },
    ];
  }, [pool, value, current?.triggerLabel]);

  if (searchable) {
    return (
      <EntitySelector
        title={rowLabel}
        config={config}
        onValueChange={(next) => onSelect(next[0]?.id ?? null)}
        value={selections}
      >
        <EntitySelectorTrigger
          emphasized={emphasized}
          fill={fullWidth}
          ghost={ghost}
          hoverBorder={hoverBorder}
          leadingGutter={gutter}
          placeholder={placeholder}
          size={toEntityTriggerSize(size === "md" ? "default" : size)}
          weight={valueWeight === "regular" ? undefined : valueWeight}
        />
        <EntitySelectorContent maxHeight={FIELD_POPUP_MAX_HEIGHT}>
          <EntitySelectorInput />
          <EntitySelectorPool value={OPTION_POOL_ID} />
          <EntitySelectorEmpty>{emptyText}</EntitySelectorEmpty>
        </EntitySelectorContent>
      </EntitySelector>
    );
  }

  // Select is value-keyed and ships one trigger face: a trigger plus a flat or
  // grouped popup, with no in-popup search field and no submenus.
  return (
    <Select
      onValueChange={(next: string | null) => onSelect(next || null)}
      value={current?.value ?? ""}
    >
      <SelectTrigger
        className={[
          fullWidth ? undefined : "w-auto",
          ghost ? "border-transparent bg-transparent shadow-none" : undefined,
          emphasized ? "not-dark:bg-muted" : undefined,
          hoverBorder || ghost ? "hover:border-muted-foreground/60" : undefined,
          valueWeight === "medium" ? "font-medium" : undefined,
          valueWeight === "semibold" ? "font-semibold" : undefined,
          valueWeight === "bold" ? "font-bold" : undefined,
        ]
          .filter(Boolean)
          .join(" ")}
        size={size === "md" ? "default" : size === "xs" ? "sm" : size}
      >
        {/* Prefer the provider's brand logo; `icon` stays the fallback for a
            provider we bundle no logo for (e.g. Webhook). */}
        {current?.logo ? (
          <BrandLogoTile logo={current.logo} />
        ) : current?.icon ? (
          <Icon name={current.icon} size={iconSize} />
        ) : null}
        {/* Base UI's Value renders the raw VALUE unless it is given a formatter
            (or Root is given an `items` map) — so without this the trigger shows
            the option's id: a connection id in the Source row, "CONNECTION_TRIGGERED"
            in the Event row. `triggerLabel` first, matching the searchable path. */}
        <SelectValue placeholder={placeholder}>
          {(selected: string | null) => {
            const option = findOption(options, selected);
            return option?.triggerLabel ?? option?.label ?? placeholder;
          }}
        </SelectValue>
      </SelectTrigger>
      <SelectPopup className={searchable ? "min-w-100" : undefined}>
        <GroupedOptions options={options} />
      </SelectPopup>
    </Select>
  );
}

// Renders a SelectItem (a selectable leaf).
function OptionItem({ option }: { option: Option }) {
  return (
    <SelectItem disabled={option.disabled} value={option.value}>
      {/* Brand logo first; the registry `icon` is the fallback. */}
      {option.logo ? (
        <BrandLogoTile logo={option.logo} />
      ) : option.icon ? (
        <Icon name={option.icon} size="xs" />
      ) : null}
      {option.label}
      {/* Same muted inline subtitle the EntitySelector rows render, so the two
          FieldSelect paths read as one system. */}
      {option.description ? (
        <Text size="xs" variant="muted">
          {option.description}
        </Text>
      ) : null}
    </SelectItem>
  );
}

// An explicit "Any …" row pinned above an entity pool's list — selecting it
// clears the filter (the trigger config stores nothing for "any"). The
// sentinel id never persists; onChange handlers map it to null.
export const ANY_POOL_ITEM_ID = "__any__";
export function anyPoolItem(title: string): EntityItemConfig {
  return {
    id: ANY_POOL_ITEM_ID,
    title,
    avatar: {
      render: () => (
        <Box width={24} height={24} align="center" justify="center">
          <Icon color="muted" name="circle-dashed" size="md" />
        </Box>
      ),
    },
    keywords: ["any"],
  };
}

// Find a pool item by id — pinned rows (e.g. the dynamic user references
// above a members list) live beside the regular items, so selection
// round-tripping must scan both.
function findPoolItem(pool: EntityPoolConfig, id: string | null) {
  if (!id) return null;
  return (
    pool.items.find((i) => i.id === id) ??
    pool.pinnedItems?.find((i) => i.id === id) ??
    null
  );
}

// Find an option by value — used to resolve the trigger's display value. Every
// option is a member of the one array (headings are strings on the options, not
// wrapper rows), so this sees everything selectable.
export function findOption(options: Option[], value: string | null): Option | null {
  if (!value) return null;
  return options.find((o) => o.value === value) ?? null;
}

// One labelled group per section, with a nested labelled group per subsection,
// over the rows `optionsToPool` also renders — so the two paths agree on order.
// With no groups at all it stays a flat list, which is what the trigger's Source
// dropdown is. Exported for the add-step picker, which shares this select body.
export function GroupedOptions({ options }: { options: Option[] }) {
  const sections = runsBy(groupOptionRows(options), "section");

  // A subsection is a nested labelled group; a section's un-subsectioned rows sit
  // directly in it. There is at most one bare run per section, so its key is
  // unique among its siblings.
  const body = (sectionRows: OptionRow[]) =>
    runsBy(sectionRows, "subsection").map((run) => {
      const items = run.map((row) => (
        <OptionItem key={row.option.value} option={row.option} />
      ));
      const subsection = run[0]?.subsection;
      return subsection ? (
        <SelectGroup key={subsection}>
          <SelectGroupLabel>{subsection}</SelectGroupLabel>
          {items}
        </SelectGroup>
      ) : (
        <Fragment key="__bare">{items}</Fragment>
      );
    });

  // Nothing is grouped → a flat list, with no group wrapper at all.
  const only = sections.length === 1 ? sections[0] : undefined;
  if (only && only[0]?.section === undefined) return <>{body(only)}</>;

  // Separator between adjacent sections — never before the first or after the last.
  return (
    <>
      {sections.map((sectionRows, i) => {
        const section = sectionRows[0]?.section;
        return (
          <Fragment key={section ?? "__ungrouped"}>
            {i > 0 ? <SelectSeparator /> : null}
            <SelectGroup>
              {section ? <SelectGroupLabel>{section}</SelectGroupLabel> : null}
              {body(sectionRows)}
            </SelectGroup>
          </Fragment>
        );
      })}
    </>
  );
}

// The entity-pool dropdown for the fields layout — standard EntitySelector
// trigger (chevron), backed by avatar/icon pools.
export function FieldEntitySelect({
  pools,
  value,
  placeholder,
  onChange,
  size = "sm",
}: Omittable<{
  pools: EntityPoolConfig[];
  value: string | null;
  placeholder: string;
  onChange: (id: string | null) => void;
  size?: "xs" | "sm" | "md" | "lg";
}>) {
  const rowLabel = useContext(FieldRowLabelContext);
  const config = useMemo<EntitySelectorConfig>(() => ({ pools, maxSelections: 1 }), [pools]);
  const { entityOf, request } = useMemberDirectory();
  const selections = useMemo<EntitySelection[]>(() => {
    if (!value) return [];
    for (const p of pools) {
      const entity = findPoolItem(p, value);
      if (entity) return [{ id: value, poolId: p.id, entity }];
    }
    // The member pool is PAGED. A member outside the loaded page is absent from
    // `items`, and returning [] here renders the trigger as an empty
    // "Select…" placeholder even though the field holds a value — the control
    // reads as unset and the author re-picks a recipient that was never lost.
    // Resolve them through the member directory instead.
    //
    // Only a MEMBER can miss this way: the team pool is not paged, so a team id
    // is always among its items. That is why the synthetic selection is
    // attributed to the first pool, which is the members pool on every field
    // that mixes the two.
    request([value]);
    const resolved = entityOf(value);
    const owner = pools[0];
    if (!resolved || !owner || resolved.kind !== "user") return [];
    return [
      {
        id: value,
        poolId: owner.id,
        entity: {
          id: value,
          title: resolveEntityLabel(resolved),
          avatar: {
            render: () => (
              <EntityTag entity={resolved} display="visual" interactive={false} />
            ),
          },
        },
      },
    ];
  }, [pools, value, entityOf, request]);
  const gutter = useFieldGutter();

  return (
    <EntitySelector
      title={rowLabel}
      config={config}
      onValueChange={(next) => onChange(next[0]?.id ?? null)}
      value={selections}
    >
      <EntitySelectorTrigger
        fill
        ghost
        leadingGutter={gutter}
        placeholder={placeholder}
        // The particle trigger runs a 3-step scale; clamp the field-control
        // scale onto it the same way the concrete selectors do.
        size={toEntityTriggerSize(size === "md" ? "default" : size)}
      />
      <EntitySelectorContent maxHeight={FIELD_POPUP_MAX_HEIGHT}>
        <EntitySelectorInput />
        {pools.length > 1 ? <EntitySelectorAutoTabs /> : null}
        {pools.map((p) => (
          <EntitySelectorPool key={p.id} value={p.id} />
        ))}
        <EntitySelectorEmpty>Nothing found</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}

// The multi-select variant of `FieldEntitySelect` — same ghost trigger and
// pool content, but keeps an array of selected ids (e.g. a condition's
// "assignee is any of …"). Selections render as removable chips in the trigger.
export function FieldMultiEntitySelect({
  pools,
  value,
  placeholder,
  onChange,
  size = "sm",
}: Omittable<{
  pools: EntityPoolConfig[];
  value: string[];
  placeholder: string;
  onChange: (ids: string[]) => void;
  size?: "xs" | "sm" | "md" | "lg";
}>) {
  const rowLabel = useContext(FieldRowLabelContext);
  const config = useMemo<EntitySelectorConfig>(() => ({ pools }), [pools]);
  const selections = useMemo<EntitySelection[]>(() => {
    const result: EntitySelection[] = [];
    for (const id of value) {
      for (const p of pools) {
        const entity = findPoolItem(p, id);
        if (entity) {
          result.push({ id, poolId: p.id, entity });
          break;
        }
      }
    }
    return result;
  }, [pools, value]);
  const gutter = useFieldGutter();

  return (
    <EntitySelector
      title={rowLabel}
      config={config}
      onValueChange={(next) => onChange(next.map((s) => s.id))}
      value={selections}
    >
      <EntitySelectorTrigger
        chips
        fill
        ghost
        leadingGutter={gutter}
        placeholder={placeholder}
        // Same clamp as FieldEntitySelect — the particle scale is 3 steps.
        size={toEntityTriggerSize(size === "md" ? "default" : size)}
      />
      <EntitySelectorContent maxHeight={FIELD_POPUP_MAX_HEIGHT}>
        <EntitySelectorInput />
        {pools.length > 1 ? <EntitySelectorAutoTabs /> : null}
        {pools.map((p) => (
          <EntitySelectorPool key={p.id} value={p.id} />
        ))}
        <EntitySelectorEmpty>Nothing found</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}

// Whether a From/To value filter is actually set — null / "" / [] all read as
// "any". Mirrors the server's matchesFieldValue (runner/trigger-filter.ts).
// A checkbox's `false` IS a filter ("to Unchecked"), so booleans count.
function hasValueFilter(value: unknown): boolean {
  if (value == null || value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

// Sentinel option that resets a From/To filter back to "any" — never stored.
const ANY_FILTER = "__any__";

// A From/To transition filter for the "Field changed" event. The control
// follows the field's type (see fieldChangeFilterMode) and always carries an
// explicit "Any" state: select fields get an "Any value" option atop their
// option list, checkbox gets Any change / Checked / Unchecked, members get a
// clearable picker, and numbers/money read an empty input as "any".
function ValueFilterEditor({
  field,
  value,
  onChange,
  memberPools,
}: {
  field: CustomFieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  memberPools: EntityPoolConfig[];
}) {
  switch (field.type) {
    case "select": {
      const options: Option[] = [
        { value: ANY_FILTER, label: "Any value" },
        ...(field.config?.options ?? []).map(
          (o: { value?: string | null; label?: string | null }) => ({
            value: String(o?.value ?? o?.label ?? ""),
            label: String(o?.label ?? o?.value ?? ""),
          }),
        ),
      ];
      return (
        <FieldSelect
          value={typeof value === "string" ? value : null}
          options={options}
          placeholder="Any value"
          size="lg"
          ghost
          onSelect={(v) => onChange(v && v !== ANY_FILTER ? v : undefined)}
        />
      );
    }
    case "member":
      return (
        <Box group="token" position="relative">
          <FieldEntitySelect
            pools={memberPools}
            value={typeof value === "string" ? value : null}
            placeholder="Any member"
            size="lg"
            onChange={(id) => onChange(id ?? undefined)}
          />
          {typeof value === "string" && value ? (
            <ClearBadge onClear={() => onChange(undefined)} />
          ) : null}
        </Box>
      );
    case "checkbox":
      return (
        <FieldSelect
          value={value === true ? "checked" : value === false ? "unchecked" : null}
          options={[
            { value: ANY_FILTER, label: "Any change" },
            { value: "checked", label: "Checked" },
            { value: "unchecked", label: "Unchecked" },
          ]}
          placeholder="Any change"
          size="lg"
          ghost
          onSelect={(v) =>
            onChange(v === "checked" ? true : v === "unchecked" ? false : undefined)
          }
        />
      );
    // Typed through the formatted editor, but travelling in the draft as the
    // decimal string every value filter carries.
    case "number":
    case "money":
      return (
        <AmountInput
          {...(field.type === "money"
            ? moneyInputProps(field.config?.currency)
            : numberInputProps(field.config))}
          aria-label={field.name}
          value={toNumericValue(value)}
          onValueChange={(next) => onChange(next === null ? undefined : String(next))}
          placeholder="Any value"
          size="lg"
        />
      );
    default:
      return null;
  }
}

// The editable "When a … " trigger sentence. Manages its own selections,
// hydrates from `initial`, and reports a TriggerDraft on every change. Reused
// by the create wizard and the canvas trigger panel.
export function TriggerSentence({
  initial,
  onChange,
  layout = "sentence",
  hideSource = false,
}: Omittable<{
  initial?: { sourceKey: string | null; config: Record<string, unknown> };
  onChange: (draft: TriggerDraft) => void;
  // "sentence" — the inline "When a … " prose (text builder, side panel).
  // "fields" — stacked Source / Event / related dropdowns (the node settings
  // drawer). Same state + draft logic; only the presentation differs.
  layout?: "sentence" | "fields";
  // Sentence layout only: render the subject as static text instead of a source
  // dropdown (the source is chosen in the trigger's settings drawer).
  hideSource?: boolean;
}>) {
  const cfg = initial?.config ?? {};
  // Inbound-capable connections for the Source dropdown. `byId` also round-trips
  // a saved connection-sourced trigger (stored connectionId → name + topics).
  const { sources: inboundSources, byId: inboundById } = useAutomationInboundSources();
  // The Source selector's value. Normally the issue entity ("project"); for a
  // connection-sourced trigger it's the connectionId — that config carries no
  // `entity`, so seed it from config.connectionId on reopen.
  const [entity, setEntity] = useState<string>(
    typeof cfg.entity === "string"
      ? cfg.entity
      : initial?.sourceKey === INTEGRATION_INBOUND_SOURCE_KEY &&
          typeof cfg.connectionId === "string"
        ? cfg.connectionId
        : DEFAULT_ENTITY,
  );
  // The issue-event state. Integration mode is driven by `entity`, not by this,
  // so seed a valid issue default even for a saved integration trigger — that
  // way switching the Source back to Project always lands on a real event.
  const [sourceKey, setSourceKey] = useState<string | null>(
    !initial?.sourceKey || initial.sourceKey === INTEGRATION_INBOUND_SOURCE_KEY
      ? DEFAULT_SOURCE_KEY
      : initial.sourceKey,
  );
  // The chosen provider event topic when the Source is a connection. Drives the
  // Event dropdown's value in integration mode (in issue mode `sourceKey` does).
  const [topic, setTopic] = useState<string | null>(
    typeof cfg.topic === "string" ? cfg.topic : null,
  );
  // Integration mode: the selected Source is an inbound connection rather than
  // an issue entity, so the config becomes { connectionId, topic } and every
  // issue-specific row hides (they gate on `action`, null for this source).
  //
  // Detected as "not a known issue entity" rather than by looking the id up in
  // `inboundById` — that map is empty until the query resolves, and stays empty
  // for a connection since deleted or disabled. Either would briefly read an
  // existing integration trigger as an issue trigger and clobber its config on
  // mount.
  const isIntegration = entity !== "" && !ENTITIES.some((e) => e.value === entity);
  // "Stage changed" — a single workflow scope for the common within-workflow
  // case. Null means "any workflow".
  const [workflowId, setWorkflowId] = useState<string | null>(
    typeof cfg.workflowId === "string" ? cfg.workflowId : null,
  );
  // From/To stage filters — shared by "Stage changed" (scoped to the single
  // workflow above) and "Workflow changed" (scoped per side below), since the
  // config keys are the same and only one event is active at a time. Null
  // means "any stage".
  const [stageId, setStageId] = useState<string | null>(
    typeof cfg.toStageId === "string" ? cfg.toStageId : null,
  );
  const [fromStageId, setFromStageId] = useState<string | null>(
    typeof cfg.fromStageId === "string" ? cfg.fromStageId : null,
  );
  // "Workflow changed" — the From/To workflow sides of the crossing. Null
  // means "any workflow".
  const [fromWorkflowId, setFromWorkflowId] = useState<string | null>(
    typeof cfg.fromWorkflowId === "string" ? cfg.fromWorkflowId : null,
  );
  const [toWorkflowId, setToWorkflowId] = useState<string | null>(
    typeof cfg.toWorkflowId === "string" ? cfg.toWorkflowId : null,
  );
  const [assigneeId, setAssigneeId] = useState<string | null>(
    typeof cfg.assigneeId === "string" ? cfg.assigneeId : null,
  );
  const [fieldId, setFieldId] = useState<string | null>(
    typeof cfg.fieldKey === "string" ? cfg.fieldKey : null,
  );
  // "Field changed" only — optional before/after value filters on the chosen
  // field. Unset means "any"; the value shape follows the field's type.
  const [fromValue, setFromValue] = useState<unknown>(cfg.fromValue);
  const [toValue, setToValue] = useState<unknown>(cfg.toValue);

  // Entity-selector pools (cheap; reused for the related-object tokens).
  // Stage pools are workflow-scoped: the stage event shares one scope, while
  // the workflow event's From/To sides may point at DIFFERENT workflows, so
  // each side gets its own pool.
  const { pool: workflowPool } = useWorkflowPool();
  const { pool: stagePool } = useStagePool(workflowId);
  const { pool: fromStagePool } = useStagePool(fromWorkflowId);
  const { pool: toStagePool } = useStagePool(toWorkflowId);
  const { pool: userPool } = useUserPool({});
  const { pool: teamPool } = useTeamPool({});
  const { pool: customFieldPool } = useCustomFieldPool();
  const assigneePools = useMemo(() => [userPool, teamPool], [userPool, teamPool]);
  const stagePools = useMemo(
    () => [stagePool, fromStagePool, toStagePool],
    [stagePool, fromStagePool, toStagePool],
  );
  const workflowPools = useMemo(() => [workflowPool], [workflowPool]);
  const fieldPools = useMemo(() => [customFieldPool], [customFieldPool]);
  // The sentence layout's inline stage token (no pinned "Any" row).
  const scopedStagePools = useMemo(() => [stagePool], [stagePool]);
  // Fields-layout pickers carry an explicit "Any …" row pinned above the
  // list; the sentence layout keeps its bare inline tokens.
  const workflowFilterPools = useMemo(
    () => [{ ...workflowPool, pinnedItems: [anyPoolItem("Any workflow")] }],
    [workflowPool],
  );
  const stageFilterPools = useMemo(
    () => [{ ...stagePool, pinnedItems: [anyPoolItem("Any stage")] }],
    [stagePool],
  );
  const fromStageFilterPools = useMemo(
    () => [{ ...fromStagePool, pinnedItems: [anyPoolItem("Any stage")] }],
    [fromStagePool],
  );
  const toStageFilterPools = useMemo(
    () => [{ ...toStagePool, pinnedItems: [anyPoolItem("Any stage")] }],
    [toStagePool],
  );
  // Member custom fields store user ids only (no teams) — see
  // MemberFieldRenderer — so their From/To filters pick from users alone.
  const memberFilterPools = useMemo(() => [userPool], [userPool]);

  // Custom-field defs power the typed From/To value editors under the
  // "Field changed" event (same source the condition builder uses). The
  // filter mode follows the field's type — most types just fire on any
  // change; checkbox is To-only.
  const { data: cfData } = useCustomFieldsList(false);
  const defMap = useMemo(
    () => buildCustomFieldDefMap(connectionRows(cfData?.customFields)),
    [cfData],
  );
  const fieldDef = fieldId ? (defMap[fieldId] ?? null) : null;
  const filterMode = fieldChangeFilterMode(fieldDef?.type);

  // In integration mode the issue `sourceKey` is dormant, so there's no issue
  // action — which is what hides every issue-specific related row below.
  const action = useMemo(
    () => (isIntegration ? null : (ACTIONS.find((a) => a.sourceKey === sourceKey) ?? null)),
    [sourceKey, isIntegration],
  );
  const entityLabel = ENTITIES.find((e) => e.value === entity)?.label ?? "Project";
  const possessive = !!action?.possessive;

  // A callback, not a plain function: the option memos below key on it, and a
  // new identity every render would rebuild them every render.
  const actionLabel = useCallback(
    (a: ActionDef) => {
      if (a.sourceKey === "issue.assigned")
        return assigneeId ? "is assigned to" : a.label;
      if (a.sourceKey === "issue.stage_changed")
        return workflowId ? "moves to stage" : a.label;
      return a.label;
    },
    [assigneeId, workflowId],
  );
  const subjectLabel = entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1); // "Project"
  const subjectUpper = entityLabel.toUpperCase(); // "PROJECT"

  // Sentence layout (text builder): verb phrases that read inside "When a … ".
  const actionOptions = useMemo<Option[]>(
    () => ACTIONS.map((a) => ({ value: a.sourceKey, label: actionLabel(a) })),
    [actionLabel],
  );

  // Node fields layout: friendly, grouped labels. Subject-prefixed events
  // ("Project created" / "Project archived") and the group header
  // ("PROJECT MANAGEMENT") adapt to the selected source.
  const eventOptions = useMemo<Option[]>(
    () =>
      ACTIONS.map((a) => ({
        value: a.sourceKey,
        label: a.subjectLabel
          ? `${subjectLabel} ${a.subjectLabel}`
          : (a.eventLabel ?? actionLabel(a)),
        icon: a.icon,
        group: a.group ? a.group.replace("{subject}", subjectUpper) : undefined,
      })),
    [actionLabel, subjectLabel, subjectUpper],
  );

  // Source dropdown options: the built-in SalesTrak entities, then one section
  // per provider that has connections ("ACCULYNX", "WEB FORM", …). Inbound
  // capability, `active`, and feature-flag visibility are all applied
  // server-side (`loadInboundSources`), so a section exists exactly when a
  // provider the workspace can see has at least one usable connection —
  // nothing to re-check here. Providers sort by name and connections by name
  // within each, because the server returns rows in storage order (which
  // interleaves providers). With no connections at all the options stay the
  // bare flat `Project` list — no group headers, unchanged UX.
  const sourceOptions = useMemo<Option[]>(() => {
    if (inboundSources.length === 0) return SOURCE_OPTIONS;
    const byProvider = new Map<string, InboundSource[]>();
    for (const s of inboundSources) {
      const forProvider = byProvider.get(s.providerSlug);
      if (forProvider) forProvider.push(s);
      else byProvider.set(s.providerSlug, [s]);
    }
    const connections = [...byProvider.values()]
      // Every group was seeded with its first connection above, so index 0 is
      // present by construction.
      .sort((a, b) =>
        at(a, 0, "provider group").providerName.localeCompare(
          at(b, 0, "provider group").providerName,
        ),
      )
      .flatMap((forProvider) =>
        [...forProvider]
          .sort((a, b) => a.connectionName.localeCompare(b.connectionName))
          .map((s) => ({
            value: s.connectionId,
            // A disabled connection is still LISTED — an automation already
            // triggered by one has to render its Source — but is marked so, and
            // `disabled` makes the row unselectable: it can be shown, never
            // newly chosen. `findOption` still resolves it, so the collapsed
            // trigger keeps displaying the saved value.
            label: s.active ? s.connectionName : `${s.connectionName} (disabled)`,
            disabled: !s.active,
            // Prefer the provider's brand logo; `icon` stays the fallback for a
            // provider we bundle no logo for (e.g. Webhook).
            icon: s.providerIcon as IconName,
            logo: brandLogo(s.providerSlug),
            group: s.providerName.toUpperCase(),
            // Findable by provider as well as by connection name. The
            // space-less form is what makes a "WebHook" query hit the "Webhook"
            // provider; the slug covers a query typed the registry's way.
            keywords: [
              s.providerName,
              s.providerName.replace(/\s+/g, ""),
              s.providerSlug,
            ],
          })),
      );
    return [...ENTITIES.map((e) => ({ ...e, group: "SALESTRAK" })), ...connections];
  }, [inboundSources]);

  // The connection currently selected as Source (integration mode), and the
  // Event dropdown's topic options for it.
  const inboundSource = isIntegration ? (inboundById.get(entity) ?? null) : null;
  const topicOptions = useMemo<Option[]>(
    () => (inboundSource?.events ?? []).map((e) => ({ value: e.key, label: e.label })),
    [inboundSource],
  );

  const resetRelated = () => {
    setWorkflowId(null);
    setStageId(null);
    setFromStageId(null);
    setFromWorkflowId(null);
    setToWorkflowId(null);
    setAssigneeId(null);
    setFieldId(null);
    setFromValue(undefined);
    setToValue(undefined);
  };

  const poolTitle = (poolList: EntityPoolConfig[], id: string): string | null => {
    for (const p of poolList) {
      const item = findPoolItem(p, id);
      if (item) return item.title;
    }
    return null;
  };
  const resolveName = (entityType: string, id: string): string | null => {
    switch (entityType) {
      case "stage":
        return poolTitle(stagePools, id);
      case "workflow":
        return poolTitle(workflowPools, id);
      case "assignee":
        return poolTitle(assigneePools, id);
      case "customField":
        return poolTitle(fieldPools, id);
      case "connection":
        {
          const source = inboundById.get(id);
          return source ? connectionDisplayName(source) : null;
        }
      case "inboundTopic":
        return inboundSource?.events.find((e) => e.key === id)?.label ?? null;
      default:
        return null;
    }
  };

  // Report the current draft upward whenever anything changes.
  useEffect(() => {
    // Integration mode: the Source is a connection. Emit the connection-sourced
    // trigger ({ connectionId, topic } under integration.inbound) and skip all
    // the issue-specific config below. `topic` is required at go-live, so the
    // draft is complete only once an event is chosen.
    if (isIntegration) {
      const config: Record<string, unknown> = { connectionId: entity };
      if (topic) config.topic = topic;
      const topicLabel = inboundSource?.events.find((e) => e.key === topic)?.label ?? null;
      // Provider-qualified, so the auto-generated name and summary read the same
      // as the canvas caption and the text summary ("AccuLynx SW Contracts").
      const connName = inboundSource ? connectionDisplayName(inboundSource) : null;
      const event = topicLabel ?? topic ?? "an event";
      // A connection that hasn't loaded (or was deleted/disabled) can't be
      // named, so fall back rather than baking a wrong name into the automation.
      onChange({
        sourceKey: INTEGRATION_INBOUND_SOURCE_KEY,
        config,
        complete: !!entity && !!topic,
        name: connName
          ? `When the ${connName} connection receives ${event}`
          : "Untitled automation",
        summary: connName ? `the ${connName} connection receives ${event}` : null,
      });
      return;
    }
    const config: Record<string, unknown> = { entity };
    // Stage-changed filters are independent — a workflow alone, a From stage,
    // a To stage, or any combination; unset means "any".
    if (action?.related === "stage") {
      if (workflowId) config.workflowId = workflowId;
      if (fromStageId) config.fromStageId = fromStageId;
      if (stageId) config.toStageId = stageId;
    }
    // The crossing's stage filters only persist under a specific workflow —
    // the pickers are hidden otherwise, and a stale stage id from a hidden
    // row must not keep filtering.
    if (action?.related === "workflow") {
      if (fromWorkflowId) config.fromWorkflowId = fromWorkflowId;
      if (fromWorkflowId && fromStageId) config.fromStageId = fromStageId;
      if (toWorkflowId) config.toWorkflowId = toWorkflowId;
      if (toWorkflowId && stageId) config.toStageId = stageId;
    }
    if (action?.related === "assignee" && assigneeId) config.assigneeId = assigneeId;
    if (action?.related === "field" && fieldId) {
      config.fieldKey = fieldId;
      // From/To transition filters — omitted when unset ("any") and only for
      // field types that support them (checkbox is To-only).
      if (filterMode === "fromTo" && hasValueFilter(fromValue)) config.fromValue = fromValue;
      if (filterMode !== "none" && hasValueFilter(toValue)) config.toValue = toValue;
    }
    const relatedComplete =
      !action ||
      action.related === "none" ||
      action.related === "assignee" ||
      action.related === "stage" ||
      action.related === "workflow" ||
      (action.related === "field" && !!fieldId);
    const complete = !!entity && !!action && relatedComplete;

    const name = action
      ? possessive
        ? `When a ${entityLabel}'s ${action.label}`
        : `When a ${entityLabel} ${actionLabel(action)}`
      : "Untitled automation";

    const summary = summarizeTrigger(sourceKey, config, resolveName);

    onChange({ sourceKey, config, complete, name, summary });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the sentence inputs; onChange and resolveName are stable callbacks read at run time
  }, [
    entity,
    sourceKey,
    workflowId,
    stageId,
    fromStageId,
    fromWorkflowId,
    toWorkflowId,
    assigneeId,
    fieldId,
    fromValue,
    toValue,
    filterMode,
    assigneePools,
    stagePools,
    workflowPools,
    fieldPools,
    isIntegration,
    topic,
    inboundSource,
  ]);

  if (layout === "fields") {
    return (
      <Box direction="column">
        {/* Body: the source (entity type), the event (never empty) and its
            related config. The gutter provider aligns every field's value
            text to one column. */}
        <FieldGutterProvider>
        <Box direction="column" gap="md" padding="md">
          <FieldRow label="Source">
            <FieldSelect
              value={entity}
              options={sourceOptions}
              placeholder="Select source"
              size="lg"
              ghost
              // Only worth a search field once integration connections widen the
              // list past the single "Project" row.
              searchable={inboundSources.length > 0}
              searchPlaceholder="Search sources…"
              emptyText="No sources found"
              onSelect={(value) => {
                const next = value ?? DEFAULT_ENTITY;
                setEntity(next);
                const src = inboundById.get(next);
                if (src) {
                  // Entering (or moving between) connections: default the topic
                  // to the first event, keeping the current one only if the new
                  // provider still offers it.
                  setTopic((prev) =>
                    src.events.some((e) => e.key === prev)
                      ? prev
                      : (src.events[0]?.key ?? null),
                  );
                } else {
                  // Back to an issue entity — the issue `sourceKey` takes over.
                  setTopic(null);
                }
              }}
            />
          </FieldRow>
          {/* Inset hairline between the source (entity type) and the event —
              the parent's md padding keeps it off the panel edges. */}
          <Separator />
          <FieldRow label="Event">
            <FieldSelect
              value={isIntegration ? topic : sourceKey}
              options={isIntegration ? topicOptions : eventOptions}
              placeholder="Select…"
              size="lg"
              iconSize="md"
              valueWeight="medium"
              hoverBorder
              emphasized
              searchable
              searchPlaceholder="Search events…"
              emptyText="No events found"
              onSelect={(value) => {
                if (!value) return; // the event can't be cleared
                if (isIntegration) {
                  setTopic(value);
                } else {
                  setSourceKey(value);
                  resetRelated();
                }
              }}
            />
          </FieldRow>
          {action?.related === "field" && (
            <>
              <FieldRow label="Field">
                <FieldEntitySelect
                  pools={fieldPools}
                  value={fieldId}
                  placeholder="Any field"
                  size="lg"
                  onChange={(id) => {
                    setFieldId(id);
                    // A different field means a different value type — stale
                    // From/To filters would be the wrong shape.
                    setFromValue(undefined);
                    setToValue(undefined);
                  }}
                />
              </FieldRow>
              {/* Transition filters — only for field types that can name a
                  value (select/member/number/money; checkbox is To-only).
                  Every control defaults to an explicit "Any", mirroring the
                  stage-changed From/To stage pair. Other types (text, dates,
                  photos, …) simply fire on any change. */}
              {fieldDef && filterMode !== "none" ? (
                <>
                  {filterMode === "fromTo" ? (
                    <FieldRow label="From">
                      <ValueFilterEditor
                        field={fieldDef}
                        value={fromValue}
                        onChange={setFromValue}
                        memberPools={memberFilterPools}
                      />
                    </FieldRow>
                  ) : null}
                  <FieldRow label="To">
                    <ValueFilterEditor
                      field={fieldDef}
                      value={toValue}
                      onChange={setToValue}
                      memberPools={memberFilterPools}
                    />
                  </FieldRow>
                </>
              ) : null}
            </>
          )}
          {/* The common within-workflow case: one workflow scope, From/To
              stages inside it. Cross-workflow precision lives on the
              "Workflow changed" event below. */}
          {action?.related === "stage" && (
            <>
              <FieldRow label="Workflow">
                <FieldEntitySelect
                  pools={workflowFilterPools}
                  value={workflowId}
                  placeholder="Any workflow"
                  size="lg"
                  onChange={(id) => {
                    setWorkflowId(id === ANY_POOL_ITEM_ID ? null : id);
                    // Stages are workflow-scoped — picks from the old
                    // workflow would be stale under the new one.
                    setStageId(null);
                    setFromStageId(null);
                  }}
                />
              </FieldRow>
              {workflowId ? (
                <>
                  <FieldRow label="From">
                    <FieldEntitySelect
                      pools={stageFilterPools}
                      value={fromStageId}
                      placeholder="Any stage"
                      size="lg"
                      onChange={(id) =>
                        setFromStageId(id === ANY_POOL_ITEM_ID ? null : id)
                      }
                    />
                  </FieldRow>
                  <FieldRow label="To">
                    <FieldEntitySelect
                      pools={stageFilterPools}
                      value={stageId}
                      placeholder="Any stage"
                      size="lg"
                      onChange={(id) =>
                        setStageId(id === ANY_POOL_ITEM_ID ? null : id)
                      }
                    />
                  </FieldRow>
                </>
              ) : null}
            </>
          )}
          {/* Cross-workflow moves. Each side is a workflow + stage pair —
              the stage row appears once its workflow is specific, so the
              crossing can be pinned end-to-end ("from Sales › Closed Won to
              Production › Install") while the simple case stays two rows. */}
          {action?.related === "workflow" && (
            <>
              <FieldRow label="From workflow">
                <FieldEntitySelect
                  pools={workflowFilterPools}
                  value={fromWorkflowId}
                  placeholder="Any workflow"
                  size="lg"
                  onChange={(id) => {
                    setFromWorkflowId(id === ANY_POOL_ITEM_ID ? null : id);
                    setFromStageId(null);
                  }}
                />
              </FieldRow>
              {fromWorkflowId ? (
                <FieldRow label="From stage">
                  <FieldEntitySelect
                    pools={fromStageFilterPools}
                    value={fromStageId}
                    placeholder="Any stage"
                    size="lg"
                    onChange={(id) =>
                      setFromStageId(id === ANY_POOL_ITEM_ID ? null : id)
                    }
                  />
                </FieldRow>
              ) : null}
              <FieldRow label="To workflow">
                <FieldEntitySelect
                  pools={workflowFilterPools}
                  value={toWorkflowId}
                  placeholder="Any workflow"
                  size="lg"
                  onChange={(id) => {
                    setToWorkflowId(id === ANY_POOL_ITEM_ID ? null : id);
                    setStageId(null);
                  }}
                />
              </FieldRow>
              {toWorkflowId ? (
                <FieldRow label="To stage">
                  <FieldEntitySelect
                    pools={toStageFilterPools}
                    value={stageId}
                    placeholder="Any stage"
                    size="lg"
                    onChange={(id) =>
                      setStageId(id === ANY_POOL_ITEM_ID ? null : id)
                    }
                  />
                </FieldRow>
              ) : null}
            </>
          )}
          {action?.related === "assignee" && (
            <FieldRow label="Assignee">
              <FieldEntitySelect
                pools={assigneePools}
                value={assigneeId}
                placeholder="Anyone"
                size="lg"
                onChange={setAssigneeId}
              />
            </FieldRow>
          )}
        </Box>
        </FieldGutterProvider>
      </Box>
    );
  }

  return (
    <Box direction="row" wrap align="center" justify="center" gap="xs">
      <Word>When a</Word>
      {hideSource ? (
        <Word>{possessive ? `${entityLabel}'s` : entityLabel}</Word>
      ) : (
        <InlineSelect
          value={entity}
          options={ENTITIES}
          placeholder="entity"
          displayLabel={possessive ? `${entityLabel}'s` : undefined}
          tooltip="The entity type this automation watches"
          onSelect={(value) => setEntity(value ?? DEFAULT_ENTITY)}
        />
      )}
      {action?.related === "field" && (
        <InlineEntitySelect
          pools={fieldPools}
          value={fieldId}
          placeholder="…"
          onChange={(id) => {
            setFieldId(id);
            // A different field means a different value type — stale From/To
            // filters (set in the node drawer) would be the wrong shape.
            setFromValue(undefined);
            setToValue(undefined);
          }}
        />
      )}
      <InlineSelect
        value={sourceKey}
        options={actionOptions}
        placeholder="…"
        displayLabel={possessive && fieldId ? "is changed" : undefined}
        onSelect={(value) => {
          setSourceKey(value);
          resetRelated();
        }}
      />
      {action?.related === "stage" && (
        <>
          <InlineEntitySelect
            pools={workflowPools}
            value={workflowId}
            placeholder="…"
            onChange={(id) => {
              setWorkflowId(id);
              setStageId(null);
              setFromStageId(null);
            }}
          />
          {workflowId ? (
            <>
              <Word>›</Word>
              <InlineEntitySelect
                pools={scopedStagePools}
                value={stageId}
                placeholder="any stage"
                onChange={setStageId}
              />
            </>
          ) : null}
        </>
      )}
      {action?.related === "assignee" && (
        <InlineEntitySelect
          pools={assigneePools}
          value={assigneeId}
          placeholder="…"
          onChange={setAssigneeId}
        />
      )}
    </Box>
  );
}
